import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, getDocs } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import { SettingsNav } from './Branches';
import { IconPlus, IconClipboardList } from '@tabler/icons-react';

export default function Templates() {
  const navigate = useNavigate();
  const { orgId, userProfile } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  const resolvedOrgId = orgId || userProfile?.orgId;

  useEffect(() => {
    const load = async () => {
      if (!resolvedOrgId) {
        setLoading(false);
        return;
      }
      try {
        const snap = await getDocs(
          collection(db, 'organisations', resolvedOrgId, 'checkTemplates')
        );
        setTemplates(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    load();
  }, [resolvedOrgId]);

  const assetTypeColour = (type) => {
    if (!type) return { bg: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)' };
    const t = Array.isArray(type) ? type[0] : type;
    if (t?.toLowerCase().includes('vehicle')) return { bg: '#E6F1FB', color: '#185FA5' };
    if (t?.toLowerCase().includes('trailer')) return { bg: '#EAF3DE', color: '#3B6D11' };
    if (t?.toLowerCase().includes('machine') || t?.toLowerCase().includes('machinery')) return { bg: '#EEEDFE', color: '#534AB7' };
    return { bg: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)' };
  };

  const countItems = (sections) => {
    if (!sections) return 0;
    return sections.reduce((sum, s) => sum + (s.items?.length || 0), 0);
  };

  return (
    <Layout>
      <div style={{ display: 'flex', flex: 1, minHeight: '100vh' }}>
        <SettingsNav active="Templates" />
        <div style={{ flex: 1, padding: 'var(--main-padding)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
            <div>
              <h1 style={{ fontSize: 22, fontWeight: 400, marginBottom: 4 }}>Templates</h1>
              <p style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>
                {loading ? 'Loading...' : `${templates.length} template${templates.length !== 1 ? 's' : ''}`}
              </p>
            </div>
            <button
              onClick={() => navigate('/settings/templates/new')}
              style={{ background: 'var(--color-brand)', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', padding: '8px 16px', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <IconPlus size={15} stroke={1.5} /> New template
            </button>
          </div>

          {loading ? (
            <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)', padding: '32px 0', textAlign: 'center' }}>Loading templates...</div>
          ) : templates.length === 0 ? (
            <div style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '48px 32px', textAlign: 'center' }}>
              <IconClipboardList size={32} color="var(--color-text-tertiary)" stroke={1.5} style={{ marginBottom: 12 }} />
              <div style={{ fontSize: 14, fontWeight: 400, color: 'var(--color-text-primary)', marginBottom: 6 }}>No templates yet</div>
              <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)', marginBottom: 20 }}>Create your first check template to get started</div>
              <button
                onClick={() => navigate('/settings/templates/new')}
                style={{ background: 'var(--color-brand)', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', padding: '8px 20px', fontSize: 13, cursor: 'pointer' }}
              >
                Create template
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {templates.map(t => {
                const types = Array.isArray(t.assetTypes) ? t.assetTypes : [t.assetTypes];
                const itemCount = countItems(t.sections);
                return (
                  <div
                    key={t.id}
                    onClick={() => navigate(`/settings/templates/${t.id}`)}
                    style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--color-border-medium)'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--color-border-light)'}
                  >
                    <div style={{ width: 36, height: 36, borderRadius: 'var(--radius-md)', background: 'var(--color-brand-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <IconClipboardList size={18} color="var(--color-brand)" stroke={1.5} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 400, color: 'var(--color-text-primary)', marginBottom: 6 }}>{t.name}</div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        {types.filter(Boolean).map(type => {
                          const c = assetTypeColour(type);
                          return (
                            <span key={type} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: c.bg, color: c.color, textTransform: 'capitalize' }}>{type}</span>
                          );
                        })}
                        {t.estimatedMinutes && (
                          <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: '#E6F1FB', color: '#185FA5' }}>~{t.estimatedMinutes} min</span>
                        )}
                        {itemCount > 0 && (
                          <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{itemCount} check{itemCount !== 1 ? 's' : ''}</span>
                        )}
                        {t.sections?.length > 0 && (
                          <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{t.sections.length} section{t.sections.length !== 1 ? 's' : ''}</span>
                        )}
                        {t.active === false && (
                          <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: 'var(--color-bg-secondary)', color: 'var(--color-text-tertiary)' }}>Inactive</span>
                        )}
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--color-brand)' }}>Edit</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}